// src/redux/authSlice.tsx

import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../api/api";
import { AuthState, PlatformUser } from "../types";
import logger from "utils/logger";

// Thunk to fetch current user details from Django API
export const fetchUserDetails = createAsyncThunk<
  PlatformUser,
  void,
  { rejectValue: string }
>("auth/fetchUserDetails", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get("/auth/user/");
    return response.data;
  } catch (err) {
    const error = err as { response?: { data?: { message?: string } } };
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch user details"
    );
  }
});

// Persist the full ui_preferences blob to the backend (cursor_logs/042). The
// caller passes the COMPLETE merged object (the PATCH replaces the field). The
// optimistic in-memory update is done separately via the `setUiPreferences`
// reducer so the UI never waits on this round-trip; here we just write-through
// and reconcile with the server's echo on success.
export const persistUiPreferences = createAsyncThunk<
  Record<string, unknown>,
  Record<string, unknown>,
  { rejectValue: string }
>("auth/persistUiPreferences", async (uiPreferences, { rejectWithValue }) => {
  try {
    const response = await api.patch("/auth/user/", {
      ui_preferences: uiPreferences,
    });
    return (response.data?.ui_preferences ?? uiPreferences) as Record<
      string,
      unknown
    >;
  } catch (err) {
    return rejectWithValue("Failed to persist UI preferences");
  }
});

const initialState: AuthState = {
  token: null,
  refresh: null,
  user: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthTokens: (
      state,
      action: PayloadAction<{ access: string; refresh: string }>
    ) => {
      state.token = action.payload.access;
      state.refresh = action.payload.refresh;
    },
    clearToken: (state) => {
      state.token = null;
      state.refresh = null;
      state.user = null;
    },
    logout: (state) => {
      state.token = null;
      state.refresh = null;
      state.user = null;
    },
    // Optimistic, in-memory merge of UI preferences (cursor_logs/042). The
    // payload is merged over the existing blob so unrelated keys are preserved.
    setUiPreferences: (
      state,
      action: PayloadAction<Record<string, unknown>>
    ) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ui_preferences: {
            ...(state.user.ui_preferences || {}),
            ...action.payload,
          },
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchUserDetails.fulfilled,
        (state, action: PayloadAction<PlatformUser>) => {
          state.user = action.payload;
        }
      )
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.user = null;
        logger.error("Error fetching user:", action.payload);
      })
      // Reconcile with the server's authoritative echo once the PATCH lands.
      .addCase(persistUiPreferences.fulfilled, (state, action) => {
        if (state.user) {
          state.user = { ...state.user, ui_preferences: action.payload };
        }
      })
      .addCase(persistUiPreferences.rejected, (_state, action) => {
        // Non-fatal: localStorage already holds the change; it will re-sync on
        // the next successful PATCH. Just log.
        logger.error("Error persisting UI preferences:", action.payload);
      });
  },
});

export const { setAuthTokens, clearToken, logout, setUiPreferences } =
  authSlice.actions;
export default authSlice.reducer;
